import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, Search, Clock, ArrowRight, Loader2, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import PromotionSlot from '../components/PromotionSlot';
import {
  NewsListItem, NewsCategoryPublic,
  listPublicNews, listPublicCategories, newsImageUrl,
} from '../api/news';

const PAGE_SIZE = 9;

export default function NewsPage() {
  const { t, language } = useLanguage();

  const [items, setItems] = useState<NewsListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<NewsCategoryPublic[]>([]);
  const [activeCat, setActiveCat] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const title = (i: { title_ru: string; title_kk: string }) =>
    language === 'kk' ? i.title_kk || i.title_ru : i.title_ru || i.title_kk;
  const excerpt = (i: { excerpt_ru: string; excerpt_kk: string }) =>
    language === 'kk' ? i.excerpt_kk || i.excerpt_ru : i.excerpt_ru || i.excerpt_kk;
  const catTitle = (i: NewsListItem) =>
    language === 'kk' ? i.category_title_kk || i.category_title_ru : i.category_title_ru || i.category_title_kk;

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(language === 'kk' ? 'kk-KZ' : 'ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPublicNews({
        category: activeCat || undefined,
        search: search.trim() || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeCat, search, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listPublicCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  // A featured article is only highlighted on the first, unfiltered page.
  const featured = useMemo(
    () => (page === 0 && !activeCat && !search ? items.find((i) => i.is_featured) : undefined),
    [items, page, activeCat, search]
  );
  const rest = featured ? items.filter((i) => i.slug !== featured.slug) : items;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-b from-white to-slate-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-2xl bg-goszakup-600 text-white flex items-center justify-center">
              <Newspaper className="w-6 h-6" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">{t('news.title')}</h1>
          </div>
          <p className="text-gray-500 max-w-2xl">{t('news.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="flex flex-wrap gap-2 flex-1">
            <button
              onClick={() => { setActiveCat(''); setPage(0); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeCat === '' ? 'bg-goszakup-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
            >
              {t('news.allCategories')}
            </button>
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => { setActiveCat(c.key); setPage(0); }}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeCat === c.key ? 'bg-goszakup-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
              >
                {language === 'kk' ? c.title_kk || c.title_ru : c.title_ru || c.title_kk}
              </button>
            ))}
          </div>
          <div className="relative md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('news.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-goszakup-500/40"
            />
          </div>
        </div>

        <PromotionSlot placement="news_top" />
        {loading ? (
          <div className="flex justify-center items-center py-24 text-gray-400"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <Newspaper className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="text-sm">{t('news.empty')}</p>
          </div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link to={`/news/${featured.slug}`} className="group block mb-8">
                <div className="grid md:grid-cols-2 gap-0 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-video md:aspect-auto bg-gray-100 overflow-hidden">
                    {newsImageUrl(featured.cover_url) ? (
                      <img src={newsImageUrl(featured.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-12 h-12 text-gray-200" /></div>
                    )}
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                        <Star className="w-3 h-3" fill="currentColor" /> {t('news.featured')}
                      </span>
                      {catTitle(featured) && (
                        <span className="text-[11px] font-bold text-goszakup-700 bg-goszakup-50 px-2.5 py-1 rounded-full">{catTitle(featured)}</span>
                      )}
                    </div>
                    <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-2 group-hover:text-goszakup-700 transition-colors">{title(featured)}</h2>
                    <p className="text-gray-500 text-sm line-clamp-3 mb-4">{excerpt(featured)}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>{fmtDate(featured.published_at)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.reading_time} {t('news.min')}</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {rest.map((post) => (
                <Link key={post.slug} to={`/news/${post.slug}`} className="group flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:border-goszakup-200 transition-all">
                  <div className="aspect-video bg-gray-100 overflow-hidden">
                    {newsImageUrl(post.cover_url) ? (
                      <img src={newsImageUrl(post.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-10 h-10 text-gray-200" /></div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    {catTitle(post) && (
                      <span className="self-start text-[11px] font-bold text-goszakup-700 bg-goszakup-50 px-2.5 py-1 rounded-full mb-2">{catTitle(post)}</span>
                    )}
                    <h3 className="font-extrabold text-gray-900 mb-1.5 line-clamp-2 group-hover:text-goszakup-700 transition-colors">{title(post)}</h3>
                    <p className="text-gray-500 text-sm line-clamp-2 mb-4 flex-1">{excerpt(post)}</p>
                    <div className="flex items-center justify-between text-xs text-gray-400 mt-auto">
                      <span>{fmtDate(post.published_at)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.reading_time} {t('news.min')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 disabled:opacity-40 hover:bg-gray-50">
                  {t('news.prev')}
                </button>
                <span className="text-sm text-gray-500 px-2">{page + 1} / {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 disabled:opacity-40 hover:bg-gray-50">
                  {t('news.next')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
